'use client';

import {
  AppBuildType,
  EnvironmentService
} from '@/services/isomorphic/EnvironmentService';
import { usePathname, useRouter } from 'next/navigation';
import { FC, memo, PropsWithChildren, use, useCallback } from 'react';
import { formatSymbols } from '@/utils/string-utils';
import { useEffectEvent } from '@/react/hooks/useEffectEvent';
import { CommonErrorBoundary } from './CommonErrorBoundary';
import { TopErrorManagerContext } from '../devtools/TopErrorManagerContext';
import { RenderError } from '../devtools/RenderError';

/**
 * The root error boundary for the application. In production builds, it handles any rendering
 * errors not caught by descendant error boundaries by redirecting the user to the 503 page.
 */
export const RootErrorBoundary: FC<PropsWithChildren> = ({ children }) => {
  const pathname = usePathname();
  const router = useRouter();

  const handleError = useCallback(() => {
    // Don't redirect in development mode since errors are expected as changes are made
    if ((process.env.IS_DEVELOPMENT_MODE ? "dev" : "prod") === AppBuildType.DevelopmentMode)
      return;

    if (pathname !== '/503') {
      router.push('/503');
    }
  }, [router, pathname]);

  return (
    <>
      {!(process.env.NEXT_PUBLIC_APP_ENV === "prod") && <HydrationErrorInstrumentation />}
      <CommonErrorBoundary
        /**
         * We specify a `key` only for the 503 page to ensure that
         * the fallback is cleared once we have navigated to it.
         *
         * Other pages do not have a key based on the `pathname`
         * to ensure that the global provider is not remounted on
         * every navigation.
         */
        key={pathname === '/503' ? '/503' : undefined}
        onError={handleError}
        fallback={null}
      >
        {children}
      </CommonErrorBoundary>
    </>
  );
};

let isHydrationErrorInstrumented = false;

/** A component that instruments the application for hydration errors. */
const HydrationErrorInstrumentation: FC = memo(
  function HydrationErrorInstrumentation() {
    const { addError } = use(TopErrorManagerContext);

    // ensures the installations below have the latest reference to `addError`.
    const addErrorOnHydrationError = useEffectEvent(addError);

    // we install the hydration error handling during render because doing
    // it in a `useEffect` would be too late and cause us to miss errors.
    if (
      typeof window !== 'undefined' && // client only
      !isHydrationErrorInstrumented // only install once
    ) {
      isHydrationErrorInstrumented = true;
      const originalConsoleError = console.error;

      const hydrationErrorPatterns = [
        /hydration/i,
        /hydrating/i,
        // https://react.dev/errors/418,
        // https://react.dev/errors/422,
        // https://react.dev/errors/423
        /minified react error #(418|422|423)/i
      ];

      const isHydrationMessage = (arg: unknown): arg is string => {
        return (
          typeof arg === 'string' &&
          hydrationErrorPatterns.some((pattern) => pattern.test(arg))
        );
      };

      const isHydrationError = (arg: unknown): arg is Error => {
        return (
          arg instanceof Error &&
          hydrationErrorPatterns.some((pattern) => pattern.test(arg.message))
        );
      };

      console.error = (...args: Array<unknown>) => {
        originalConsoleError(...args);

        if (isHydrationMessage(args[0])) {
          const message = formatSymbols(
            ...(args as [string, ...Array<string>])
          );
          const error = new Error(message);
          error.name = 'ConsoleLoggedHydrationError';
          addErrorOnHydrationError(
            new RenderError({ error, inHydration: true })
          );
        }
      };

      window.addEventListener('error', (event) => {
        if (isHydrationError(event.error)) {
          event.error.name = 'WindowReportedHydrationError';
          addErrorOnHydrationError(
            new RenderError({ error: event.error, inHydration: true })
          );
        }
      });
    }

    return null;
  }
);
